import React, { useMemo } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';

// Phase colors matching PowerCurrentChart_V2
const PHASE_COLORS = {
  L1: '#DC143C', // Crimson
  L2: '#FF6347', // Tomato
  L3: '#FF8C69', // Salmon
  total: '#DC143C' // Use L1 color for total
};

const CurrentUtilisation = ({ data }) => {
  const processedData = useMemo(() => {
    if (!data?.energycontrol) return [];

    // Check which data source is available
    const meteredData = data.energycontrol.current_utilisation_metered || {};
    const habitaioData = data.energycontrol.current_utilisation_habitaio || {};

    // Determine which dataset to use (prefer metered if available, otherwise habitaio)
    const sourceData = Object.keys(meteredData).length > 0 ? meteredData : habitaioData;
    const isMetered = Object.keys(meteredData).length > 0;

    if (Object.keys(sourceData).length === 0) return [];

    return Object.entries(sourceData)
      .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
      .map(([date, values]) => {
        const entry = { date };

        if (isMetered) {
          // Metered data has L1, L2, L3 phases
          entry.L1_max = values.L1_max || 0;
          entry.L1_avg = values.L1_avg || 0;
          entry.L2_max = values.L2_max || 0;
          entry.L2_avg = values.L2_avg || 0;
          entry.L3_max = values.L3_max || 0;
          entry.L3_avg = values.L3_avg || 0;
          entry.fuse_max = values.fuse_max || 0;
        } else {
          // Habitaio data has phase current max and avg
          entry.total_max = Number(values.phase_current_max) || 0;
          entry.total_avg = Number(values.phase_current_avg) || 0;
          entry.fuse_max = Number(values.fuse_max) || 0;
        }

        return entry;
      });
  }, [data]);

  // Determine data source type
  const isMetered = useMemo(() => {
    if (processedData.length === 0) return false;
    return processedData[0].hasOwnProperty('L1_max');
  }, [processedData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div className="custom-tooltip bg-white p-4 border rounded shadow-lg">
        <p className="font-bold">{label}</p>
        <table className="w-full mt-2">
          <tbody>
            {payload.map((entry, index) => (
              <tr key={index}>
                <td className="py-1">
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 mr-2"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span>{entry.name}</span>
                  </div>
                </td>
                <td className="text-right font-semibold">{entry.value.toFixed(2)} A</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!processedData.length) return null;

  // Find the maximum fuse_max value across all data points
  const fuseMax = Math.max(...processedData.map(d => d.fuse_max || 0));
  const showFuseMax = fuseMax > 0;

  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={processedData}
          margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            angle={-45}
            textAnchor="end"
            height={60}
          />

          <YAxis
            label={{ value: 'Current (A)', angle: -90, position: 'insideLeft' }}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend />

          {/* Reference line for fuse max - only show if fuse_max > 0 */}
          {showFuseMax && (
            <ReferenceLine
              y={fuseMax}
              stroke="#ff0000"
              strokeDasharray="5 5"
              label={{ value: `Fuse Max (${fuseMax}A)`, position: 'right' }}
            />
          )}

          {/* Conditionally render lines based on data source */}
          {isMetered && (
            <>
              {/* L1 Phase */}
              <Line
                type="monotone"
                dataKey="L1_max"
                name="L1 Max"
                stroke={PHASE_COLORS.L1}
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="L1_avg"
                name="L1 Avg"
                stroke={PHASE_COLORS.L1}
                strokeWidth={1}
                strokeDasharray="4 2"
                dot={{ r: 3 }}
              />

              {/* L2 Phase */}
              <Line
                type="monotone"
                dataKey="L2_max"
                name="L2 Max"
                stroke={PHASE_COLORS.L2}
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="L2_avg"
                name="L2 Avg"
                stroke={PHASE_COLORS.L2}
                strokeWidth={1}
                strokeDasharray="4 2"
                dot={{ r: 3 }}
              />

              {/* L3 Phase */}
              <Line
                type="monotone"
                dataKey="L3_max"
                name="L3 Max"
                stroke={PHASE_COLORS.L3}
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="L3_avg"
                name="L3 Avg"
                stroke={PHASE_COLORS.L3}
                strokeWidth={1}
                strokeDasharray="4 2"
                dot={{ r: 3 }}
              />
            </>
          )}

          {!isMetered && (
            <>
              <Line
                type="monotone"
                dataKey="total_max"
                name="Total Max"
                stroke="#DC143C"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="total_avg"
                name="Total Avg"
                stroke="#DC143C"
                strokeWidth={1}
                strokeDasharray="4 2"
                dot={{ r: 3 }}
              />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CurrentUtilisation;
